/**
 * Build id parsing and comparison — ONE implementation for the React side, the
 * boot script in index.html and the service worker.
 *
 * A build id is `<commit>-<epoch ms>` (e.g. `88ea1c3-1782509724917`;
 * `unknown-<epoch>` when git was unavailable). scripts/inject-build-info.js
 * writes it into public/sw.js, and the Vite plugin (apps/web/buildIdPlugin.js)
 * writes the same value into build/index.html and build/build-manifest.json.
 *
 * The boot script and the service worker cannot import an ES module, so the
 * plugin compiles THIS file into one classic IIFE that sets
 * `self.SendlyBuildId = { parseBuildId, isKnownBuildId, isNewerBuildId }`.
 * Keep the source plain on purpose: no imports, no classes, no syntax that
 * needs more than the bundle's own build target to lower.
 *
 * "Unknown" is always decided by the FORMAT check below, never by comparing
 * with the `__BUILD_ID__` placeholder literal (the placeholder, an empty
 * string, null and undefined are all simply not a build id).
 */

const BUILD_ID_PATTERN = /^([0-9a-f]{7,40}|unknown)-(\d{13})$/;

/**
 * Split an id into its commit part and its epoch (ms). Both fields are `null`
 * when the id does not match the format, so a caller can read `.commit`
 * without a guard (the boot watchdog's breadcrumb does).
 *
 * @param {*} id
 * @returns {{ commit: string|null, epoch: number|null }}
 */
export function parseBuildId(id) {
  const m = typeof id === "string" ? BUILD_ID_PATTERN.exec(id) : null;
  if (!m) return { commit: null, epoch: null };
  return { commit: m[1], epoch: Number(m[2]) };
}

/**
 * A FORMAT check: `^([0-9a-f]{7,40}|unknown)-\d{13}$`. `unknown-<epoch>` is
 * known (it still sorts); the unreplaced placeholder, the empty string, null
 * and undefined are not.
 */
export function isKnownBuildId(id) {
  return parseBuildId(id).epoch !== null;
}

/**
 * True only when BOTH ids are known and the candidate's epoch is strictly
 * later. An equal epoch is not newer whatever the commit, and an unknown id on
 * either side answers false — "I cannot tell" must never read as "newer".
 */
export function isNewerBuildId(candidate, current) {
  const a = parseBuildId(candidate).epoch;
  const b = parseBuildId(current).epoch;
  if (a === null || b === null) return false;
  return a > b;
}
